// CandidateLocationForm.jsx
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { Card, CardContent, Grid, Box, Divider } from "@mui/material";
import { LocationOn as LocationIcon } from "@mui/icons-material";
import { QK } from "../../base/qk";
import { getLocations, getSubLocations } from "../../api/services/scheduling";
import CustomInput from "../FormFields/CustomInput/CustomInput";
import CustomSelect from "../FormFields/CustomSelect/CustomSelect";
import MultiSelect from "../FormFields/CustomSelect/MultiSelect";
import CustomSwitch from "../FormFields/CustomSwitch";
import CardHeaderComponent from "./CardHeaderComponent";

const CandidateLocationForm = ({
  handleChange,
  data,
  selectedArea,
  handleSelectAreaChange,
}) => {
  const [locationId, setLocationId] = useState(null);

  const { data: locations } = useQuery([QK.location], () => getLocations());
  const { data: sublocations, isLoading } = useQuery(
    [QK.subLocations, locationId],
    () => getSubLocations(locationId),
    { enabled: !!locationId }
  );

  useEffect(() => {
    if (data?.base_location) setLocationId(data.base_location);
  }, [data?.base_location]);

  const locationOptions = locations?.map((loc) => ({ value: loc.id, label: loc.name })) || [];
  const areaOptions = sublocations?.map((area) => ({ value: area.id, label: area.name })) || [];

  const safeSelectedArea = Array.isArray(selectedArea) ? selectedArea : [];
  const hasAreaError = locationId && safeSelectedArea.length === 0;

  return (
    <Card variant="outlined" sx={{ height: "100%", borderRadius: "12px", borderColor: "#E2E8F0", boxShadow: "none" }}>
      <CardHeaderComponent icon={LocationIcon} title="Location" />
      <CardContent sx={{ p: 2.5 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <CustomInput 
              label="Address" 
              name="address_1" 
              onChange={handleChange} 
              value={data?.address_1 || ""} 
              fullWidth 
              size="small" 
              placeholder="Enter street address" 
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <CustomInput 
              label="Address 2" 
              name="address_2" 
              onChange={handleChange} 
              value={data?.address_2 || ""} 
              fullWidth 
              size="small" 
              placeholder="Apartment, suite, etc." 
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <CustomInput 
              label="Postcode" 
              name="postcode" 
              onChange={handleChange} 
              value={data?.postcode || ""} 
              fullWidth 
              size="small" 
              placeholder="Enter postcode"
              inputProps={{ maxLength: 10 }}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <CustomSelect
              options={locationOptions}
              label="Base Location"
              name="base_location"
              value={data?.base_location || ""}
              onChange={(e) => {
                setLocationId(e.target.value);
                handleChange(e);
              }}
              fullWidth
              size="small"
              placeholder="Select Base Location"
              required
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <MultiSelect
              options={areaOptions}
              value={safeSelectedArea}
              onChange={handleSelectAreaChange}
              label="Areas"
              placeholder={locationId ? "Search and select areas..." : "Select base location first"}
              isLoading={isLoading}
              required
              fullWidth
              disabled={!locationId}
              error={hasAreaError}
              helperText={hasAreaError ? "Please select at least one area" : ""}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 1, borderColor: "#E2E8F0" }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <CustomSwitch 
                label="Access All Zone" 
                name="is_all_zones" 
                checked={!!data?.is_all_zones} 
                onChange={handleChange} 
                size="small" 
              />
              <CustomSwitch 
                label="Access My Zone" 
                name="is_my_zones" 
                checked={!!data?.is_my_zones} 
                onChange={handleChange} 
                size="small" 
              />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CandidateLocationForm;